// Copyright (c) 2026 Michael F. Kamprath
//
// settings.js -- user preferences persisted to localStorage.
//
// Tolerates missing/broken localStorage (private browsing, sandbox)
// by falling back to an in-memory store.

/** Locales the settings panel surfaces in its language picker. */
export const SUPPORTED_LOCALES = Object.freeze(['en', 'zh-CN', 'ja', 'ko']);

/**
 * Verbal-prompts mode options, ordered as they render in the panel's
 * segmented control:
 *   'all'            -- every voice line fires: intros, number voice
 *                       on tap, praise, comparator audio, player-
 *                       assist prompt, interjections.
 *   'counting-only'  -- ONLY the number voice on tap plays.  Intros,
 *                       praise, comparator, assist, interjections are
 *                       all silenced.
 *   'off'            -- every verbal voice-over is silenced, including
 *                       the counting voice.  Pop / reveal SFX still
 *                       fire (they're tap-feedback cues, not verbal).
 */
export const VERBAL_PROMPT_MODES = Object.freeze(['all', 'counting-only', 'off']);

const STORAGE_KEYS = Object.freeze({
  locale: 'ewecancount.locale',
  // Legacy boolean key for verbal prompts (pre-3-mode spec).  Migrated
  // into verbalPromptMode ('true' → 'all', 'false' → 'off') on first
  // read and removed from storage.  Kept in the map so __resetForTests
  // clears stray instances and the migration step can find it.
  verbalPromptsLegacy: 'ewecancount.verbalPrompts',
  verbalPromptMode: 'ewecancount.verbalPromptMode',
  entranceAnimations: 'ewecancount.entranceAnimations',
  maxCount: 'ewecancount.maxCount',
  activeThemes: 'ewecancount.activeThemes',
  // Legacy flat key (pre-SETTINGS-SPEC).  Migrated into the per-locale
  // dict below and removed from storage on first read.
  numeralScriptLegacy: 'ewecancount.numeralScript',
  numeralScriptByLocale: 'ewecancount.numeralScriptByLocale',
  shareStats: 'ewecancount.shareStats',
});

/**
 * Per-locale numeral-script defaults.  When a user hasn't picked a
 * script for a locale yet (either because they've never visited the
 * Numeral Script segmented control, or because they just switched to
 * a new locale), fall back to these.  Matches SETTINGS-SPEC.md §5.2.
 *
 * East-Asian-toddler pedagogy: kids in zh-CN, ja, and ko learn
 * Arabic numerals (1 2 3) for WRITTEN display first; locale-specific
 * number characters come later.  Spoken counting always uses the
 * locale's number words regardless of which script is on screen,
 * via voice-sets.json's per-script audio routing.
 *
 * So zh-CN and ko both default to 'arabic' display.  ja is the
 * exception: legacy iOS shipped 'japanese' (hiragana いち, に, さん)
 * as the default, and we preserve that pedagogically-intentional
 * choice from the original Japanese designers.
 *
 * The picker still offers all allowed scripts per locale so a parent
 * can flip the display to characters when their kid is ready to
 * practice them.
 */
const NUMERAL_SCRIPT_DEFAULTS_BY_LOCALE = Object.freeze({
  'en':    'arabic',
  'zh-CN': 'arabic',
  'ja':    'japanese',
  'ko':    'arabic',
});

/**
 * Allowed numeral-script values per locale (SETTINGS-SPEC §5.2).
 * The panel's segmented control renders in this order, and
 * setNumeralScript validates against this map -- a value not in the
 * allowed set for the given locale is rejected.  A locale missing
 * from the map is treated as "no constraint" (the panel hides the
 * control instead of rendering it).
 */
export const NUMERAL_SCRIPT_OPTIONS_BY_LOCALE = Object.freeze({
  'en':    Object.freeze(['arabic']),
  'zh-CN': Object.freeze(['arabic', 'chinese']),
  'ja':    Object.freeze(['arabic', 'chinese', 'japanese']),
  'ko':    Object.freeze(['arabic', 'chinese', 'sino-korean', 'native-korean']),
});

/**
 * Lower-bound on the max-count slider.  SETTINGS-SPEC.md §4.1 bumps
 * this from the original 1 to 4 (there's no meaningful "count to 1"
 * quiz round -- the player can't get the answer wrong).
 */
const MAX_COUNT_MIN = 4;
const MAX_COUNT_MAX = 20;

const DEFAULTS = Object.freeze({
  locale: 'en',
  verbalPromptMode: 'all',
  entranceAnimations: true,
  maxCount: 10,
  activeThemes: ['sheep', 'cows', 'horses', 'chicks', 'apples', 'flowers'],
  numeralScriptByLocale: Object.freeze({ ...NUMERAL_SCRIPT_DEFAULTS_BY_LOCALE }),
  shareStats: true,
});

function createSafeStorage() {
  let store;
  try {
    const testKey = '__ecc_test__';
    globalThis.localStorage.setItem(testKey, testKey);
    globalThis.localStorage.removeItem(testKey);
    store = globalThis.localStorage;
  } catch {
    const memory = new Map();
    store = {
      getItem: (k) => (memory.has(k) ? memory.get(k) : null),
      setItem: (k, v) => memory.set(k, String(v)),
      removeItem: (k) => memory.delete(k),
    };
  }
  return store;
}

const storage = createSafeStorage();

/** @type {Set<(state: ReturnType<typeof getAll>) => void>} */
const listeners = new Set();

function emit() {
  const snapshot = getAll();
  for (const fn of listeners) {
    try {
      fn(snapshot);
    } catch (err) {
      console.error('[settings] listener threw:', err);
    }
  }
}

// --- Locale ---------------------------------------------------------------

export function getLocale() {
  return storage.getItem(STORAGE_KEYS.locale) ?? DEFAULTS.locale;
}

export function hasStoredLocale() {
  return storage.getItem(STORAGE_KEYS.locale) !== null;
}

export function setLocale(value) {
  if (typeof value !== 'string' || value.length === 0) return false;
  if (getLocale() === value) return false;
  storage.setItem(STORAGE_KEYS.locale, value);
  emit();
  return true;
}

// --- First-boot browser-language detection --------------------------------
//
// SETTINGS-SPEC.md §5.1 "first-load default (required rule)": on the
// very first boot (no stored locale), resolve navigator.language to
// one of the supported locales, write it to storage so subsequent
// boots read the stored value.  Subsequent boots MUST NOT re-detect --
// the user may have since picked a different locale manually and
// re-detection would clobber that choice.
//
// Separated into a pure `detectBrowserLocale(nav)` + an explicit
// `ensureLocaleInitialized(nav)` call so tests can drive detection
// with mocked navigator objects without side-effectful module init.

/**
 * Map a navigator-provided language tag to one of SUPPORTED_LOCALES.
 * Case-insensitive; anything unrecognised falls through to 'en'.
 *
 * Precedence-tests from SETTINGS-SPEC.md §13:
 *   en-US      → en
 *   zh-CN      → zh-CN
 *   zh-Hans-HK → zh-CN
 *   ja-JP      → ja
 *   ko-KR      → ko
 *   fr-FR      → en
 *
 * @param {{ language?: string, languages?: string[] } | null | undefined} nav
 * @returns {'en' | 'zh-CN' | 'ja' | 'ko'}
 */
export function detectBrowserLocale(nav) {
  const raw = nav?.language
    ?? (Array.isArray(nav?.languages) ? nav.languages[0] : undefined)
    ?? '';
  const lc = String(raw).toLowerCase();
  // zh-CN / zh-Hans* / zh-SG → zh-CN
  if (lc === 'zh-cn' || lc === 'zh-sg') return 'zh-CN';
  if (lc === 'zh-hans' || lc.startsWith('zh-hans-')) return 'zh-CN';
  // ja* → ja
  if (lc === 'ja' || lc.startsWith('ja-')) return 'ja';
  // ko* → ko
  if (lc === 'ko' || lc.startsWith('ko-')) return 'ko';
  // en* → en (also the final fallback)
  if (lc === 'en' || lc.startsWith('en-')) return 'en';
  return 'en';
}

/**
 * First-boot locale initialisation.  Call ONCE at app startup
 * (src/app.js initApp) before any UI renders.  If a locale is already
 * stored, returns it unchanged.  Otherwise resolves the browser's
 * language, writes the resolved value to storage so subsequent boots
 * skip detection, and returns it.
 *
 * Does NOT fire the settings `onChange` observer -- the initialised
 * locale IS the initial state, not a user-driven change.
 *
 * @param {{ language?: string, languages?: string[] } | null | undefined} [nav]
 *   Navigator-like object; defaults to globalThis.navigator.  Tests
 *   pass a stubbed object; production code omits the argument.
 * @returns {string}  The resolved locale (stored or newly-detected).
 */
export function ensureLocaleInitialized(nav = globalThis.navigator) {
  if (hasStoredLocale()) return getLocale();
  const detected = detectBrowserLocale(nav);
  storage.setItem(STORAGE_KEYS.locale, detected);
  return detected;
}

// --- Verbal prompts (3-mode) ---------------------------------------------
//
// `verbalPromptMode` replaces the pre-spec boolean `verbalPrompts` key.
// Migration runs lazily: on any getVerbalPromptMode / setVerbalPromptMode
// call, if the legacy boolean key is in storage, it's translated
// ('true' → 'all', 'false' → 'off'), the new key is seeded, and the
// old key is removed.  Idempotent; safe to call repeatedly.
//
// Back-compat shims getVerbalPrompts / setVerbalPrompts are kept so
// callers that haven't updated to the 3-mode API still work:
//   getVerbalPrompts() returns `mode !== 'off'` (any non-silent mode).
//   setVerbalPrompts(bool) writes 'all' (for true) or 'off' (for false).

function migrateVerbalPromptsBooleanIfNeeded() {
  const legacy = storage.getItem(STORAGE_KEYS.verbalPromptsLegacy);
  if (legacy === null) return;
  // Only seed the new key if no new value is already there -- respect
  // any 3-mode choice the user has already made.
  if (storage.getItem(STORAGE_KEYS.verbalPromptMode) === null) {
    storage.setItem(
      STORAGE_KEYS.verbalPromptMode,
      legacy === 'true' ? 'all' : 'off',
    );
  }
  storage.removeItem(STORAGE_KEYS.verbalPromptsLegacy);
}

export function getVerbalPromptMode() {
  migrateVerbalPromptsBooleanIfNeeded();
  const raw = storage.getItem(STORAGE_KEYS.verbalPromptMode);
  if (raw === null) return DEFAULTS.verbalPromptMode;
  return VERBAL_PROMPT_MODES.includes(raw) ? raw : DEFAULTS.verbalPromptMode;
}

export function setVerbalPromptMode(value) {
  if (!VERBAL_PROMPT_MODES.includes(value)) return false;
  migrateVerbalPromptsBooleanIfNeeded();
  if (getVerbalPromptMode() === value) return false;
  storage.setItem(STORAGE_KEYS.verbalPromptMode, value);
  emit();
  return true;
}

/** @deprecated Back-compat shim: true when mode is not 'off'. */
export function getVerbalPrompts() {
  return getVerbalPromptMode() !== 'off';
}

/** @deprecated Back-compat shim: maps true → 'all', false → 'off'. */
export function setVerbalPrompts(value) {
  return setVerbalPromptMode(value ? 'all' : 'off');
}

/** True when counting-voice on tap should play (mode 'all' or 'counting-only'). */
export function getCountingVoiceEnabled() {
  return getVerbalPromptMode() !== 'off';
}

/** True when non-counting prompts (intros, praise, comparator, etc.) should play. */
export function getPromptsEnabled() {
  return getVerbalPromptMode() === 'all';
}

// --- Entrance animations --------------------------------------------------

export function getEntranceAnimations() {
  const raw = storage.getItem(STORAGE_KEYS.entranceAnimations);
  if (raw === null) return DEFAULTS.entranceAnimations;
  return raw === 'true';
}

export function setEntranceAnimations(value) {
  const next = Boolean(value);
  if (getEntranceAnimations() === next) return false;
  storage.setItem(STORAGE_KEYS.entranceAnimations, String(next));
  emit();
  return true;
}

// --- Max count ------------------------------------------------------------

export function getMaxCount() {
  const raw = storage.getItem(STORAGE_KEYS.maxCount);
  if (raw === null) return DEFAULTS.maxCount;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n >= MAX_COUNT_MIN && n <= MAX_COUNT_MAX
    ? n
    : DEFAULTS.maxCount;
}

export function setMaxCount(value) {
  const n = Math.max(MAX_COUNT_MIN, Math.min(MAX_COUNT_MAX, Math.round(value)));
  if (getMaxCount() === n) return false;
  storage.setItem(STORAGE_KEYS.maxCount, String(n));
  emit();
  return true;
}

/** Bounds for the max-count slider, surfaced so the panel can size its control. */
export function getMaxCountBounds() {
  return { min: MAX_COUNT_MIN, max: MAX_COUNT_MAX };
}

// --- Active themes --------------------------------------------------------

export function getActiveThemes() {
  const raw = storage.getItem(STORAGE_KEYS.activeThemes);
  if (raw === null) return [...DEFAULTS.activeThemes];
  try {
    const arr = JSON.parse(raw);
    if (Array.isArray(arr) && arr.length > 0) return arr;
  } catch { /* fall through */ }
  return [...DEFAULTS.activeThemes];
}

export function setActiveThemes(themes) {
  if (!Array.isArray(themes) || themes.length === 0) return false;
  storage.setItem(STORAGE_KEYS.activeThemes, JSON.stringify(themes));
  emit();
  return true;
}

// --- Numeral script (per-locale dict, migrated from the flat key) ---------

function readNumeralScriptDict() {
  const raw = storage.getItem(STORAGE_KEYS.numeralScriptByLocale);
  if (raw === null) return {};
  try {
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object' && !Array.isArray(parsed))
      ? parsed
      : {};
  } catch {
    return {};
  }
}

function writeNumeralScriptDict(dict) {
  storage.setItem(STORAGE_KEYS.numeralScriptByLocale, JSON.stringify(dict));
}

/**
 * Move the legacy flat `numeralScript` key into the per-locale dict
 * under the CURRENT locale, then remove the flat key.  Idempotent:
 * safe to call on every getNumeralScript / setNumeralScript invocation
 * (the `if (flatRaw === null) return` guard makes it a ~one-microsecond
 * no-op after first migration).
 *
 * Only writes to the dict if the current locale doesn't already have
 * an entry -- a user who switched locales AND set a new script between
 * the flat-era boot and this one shouldn't have their new pick
 * clobbered by the old flat value.
 */
function migrateFlatNumeralScriptIfNeeded() {
  const flat = storage.getItem(STORAGE_KEYS.numeralScriptLegacy);
  if (flat === null) return;
  const dict = readNumeralScriptDict();
  const currentLocale = getLocale();
  if (!Object.prototype.hasOwnProperty.call(dict, currentLocale)) {
    dict[currentLocale] = flat;
    writeNumeralScriptDict(dict);
  }
  storage.removeItem(STORAGE_KEYS.numeralScriptLegacy);
}

/**
 * Numeral script for the given locale (defaults to the current
 * locale).  Falls through to per-locale defaults (see
 * NUMERAL_SCRIPT_DEFAULTS_BY_LOCALE) when the user hasn't picked
 * a script for that locale yet.
 *
 * Signature stayed backwards-compatible: zero-arg callers still work.
 *
 * @param {string} [localeArg]
 * @returns {string}
 */
export function getNumeralScript(localeArg) {
  migrateFlatNumeralScriptIfNeeded();
  const locale = typeof localeArg === 'string' && localeArg ? localeArg : getLocale();
  const dict = readNumeralScriptDict();
  if (Object.prototype.hasOwnProperty.call(dict, locale)) {
    return dict[locale];
  }
  return NUMERAL_SCRIPT_DEFAULTS_BY_LOCALE[locale] ?? 'arabic';
}

/**
 * Set the numeral script for the given locale (defaults to the current
 * locale).  Writes to the per-locale dict and fires the observer.
 *
 * @param {string} value
 * @param {string} [localeArg]
 * @returns {boolean}
 */
export function setNumeralScript(value, localeArg) {
  if (typeof value !== 'string' || value.length === 0) return false;
  migrateFlatNumeralScriptIfNeeded();
  const locale = typeof localeArg === 'string' && localeArg ? localeArg : getLocale();
  // Defence-in-depth: reject values not in the allowed set for this
  // locale (SETTINGS-SPEC §5.2).  The panel UI never produces an
  // invalid pair, but a direct caller or stale storage could.  A
  // locale absent from the map is unconstrained (no rendering).
  const allowed = NUMERAL_SCRIPT_OPTIONS_BY_LOCALE[locale];
  if (allowed && !allowed.includes(value)) return false;
  const dict = readNumeralScriptDict();
  if (dict[locale] === value) return false;
  dict[locale] = value;
  writeNumeralScriptDict(dict);
  emit();
  return true;
}

/**
 * Snapshot of the full numeral-script dict (for panel rendering / debug).
 * Returns a fresh object; mutating the result does not affect storage.
 */
export function getNumeralScriptByLocale() {
  migrateFlatNumeralScriptIfNeeded();
  return { ...readNumeralScriptDict() };
}

// --- Share stats ----------------------------------------------------------

export function getShareStats() {
  const raw = storage.getItem(STORAGE_KEYS.shareStats);
  if (raw === null) return DEFAULTS.shareStats;
  return raw === 'true';
}

export function setShareStats(value) {
  const next = Boolean(value);
  if (getShareStats() === next) return false;
  storage.setItem(STORAGE_KEYS.shareStats, String(next));
  emit();
  return true;
}

// --- Composite accessors --------------------------------------------------

export function getAll() {
  return {
    locale: getLocale(),
    verbalPromptMode: getVerbalPromptMode(),
    verbalPrompts: getVerbalPrompts(),   // back-compat shim
    entranceAnimations: getEntranceAnimations(),
    maxCount: getMaxCount(),
    activeThemes: getActiveThemes(),
    numeralScript: getNumeralScript(),
    numeralScriptByLocale: getNumeralScriptByLocale(),
    shareStats: getShareStats(),
  };
}

export function onChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// --- Test helpers ---------------------------------------------------------

export function __resetForTests() {
  listeners.clear();
  for (const key of Object.values(STORAGE_KEYS)) {
    try {
      storage.removeItem(key);
    } catch { /* ignore */ }
  }
}

export const __STORAGE_KEYS = STORAGE_KEYS;
export const __DEFAULTS = DEFAULTS;
export const __NUMERAL_SCRIPT_DEFAULTS_BY_LOCALE = NUMERAL_SCRIPT_DEFAULTS_BY_LOCALE;
